import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { X } from "lucide-react";
import DOMPurify from "dompurify";

interface AdPlacement {
  id: string;
  name: string;
  placement: string;
  ad_type: string;
  image_url: string | null;
  link_url: string;
  link_target: string;
  alt_text: string | null;
  html_content: string | null;
  max_width: number | null;
  max_height: number | null;
  priority: number;
}

interface AdBannerProps {
  placement: "sidebar_right" | "homepage_banner" | "header_bar" | "footer_banner" | "between_content";
  page: string;
  className?: string;
}

export function AdBanner({ placement, page, className = "" }: AdBannerProps) {
  const [ads, setAds] = useState<AdPlacement[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchAds = async () => {
      const now = new Date().toISOString();
      const { data } = await supabase
        .from("ad_placements")
        .select("id, name, placement, ad_type, image_url, link_url, link_target, alt_text, html_content, max_width, max_height, priority, start_date, end_date, pages")
        .eq("is_active", true)
        .eq("placement", placement)
        .order("priority", { ascending: false });

      if (data) {
        // Filter by page and date range client-side
        const filtered = (data as any[]).filter(ad => {
          const pages = ad.pages as string[] | null;
          if (pages && pages.length > 0 && !pages.includes(page)) return false;
          if (ad.start_date && new Date(ad.start_date) > new Date(now)) return false;
          if (ad.end_date && new Date(ad.end_date) < new Date(now)) return false;
          return true;
        });
        setAds(filtered);

        // Track impressions
        for (const ad of filtered) {
          supabase.from("ad_placements").update({
            impression_count: (ad as any).impression_count + 1
          } as any).eq("id", ad.id).then(() => {});
        }
      }
    };
    fetchAds();
  }, [placement, page]);

  const handleClick = async (ad: AdPlacement) => {
    try {
      await supabase.from("ad_placements").update({
        click_count: ((ad as any).click_count || 0) + 1
      } as any).eq("id", ad.id);
    } catch {}
  };

  const visibleAds = ads.filter(ad => !dismissed.has(ad.id));
  if (visibleAds.length === 0) return null;

  return (
    <div className={`ad-banner-container ${className}`}>
      {visibleAds.map(ad => (
        <div key={ad.id} className="relative group mb-3" style={{ maxWidth: ad.max_width || undefined }}>
          {/* Dismiss button */}
          <button
            onClick={() => setDismissed(prev => new Set(prev).add(ad.id))}
            className="absolute -top-1.5 -right-1.5 z-10 bg-muted rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Anzeige schließen"
          >
            <X className="w-3 h-3 text-muted-foreground" />
          </button>

          {/* Ad content */}
          {ad.ad_type === "banner" && ad.image_url && (
            <a
              href={ad.link_url}
              target={ad.link_target}
              rel="noopener noreferrer sponsored"
              onClick={() => handleClick(ad)}
              className="block"
            >
              <img
                src={ad.image_url}
                alt={ad.alt_text || ad.name}
                className="rounded-lg border border-border shadow-sm hover:shadow-md transition-shadow w-full object-contain"
                style={{ maxHeight: ad.max_height || undefined }}
              />
            </a>
          )}

          {ad.ad_type === "text_link" && (
            <a
              href={ad.link_url}
              target={ad.link_target}
              rel="noopener noreferrer sponsored"
              onClick={() => handleClick(ad)}
              className="block p-3 rounded-lg border border-border bg-card hover:bg-accent transition-colors text-sm"
            >
              <p className="font-medium text-foreground">{ad.name}</p>
              {ad.alt_text && <p className="text-xs text-muted-foreground mt-1">{ad.alt_text}</p>}
            </a>
          )}

          {ad.ad_type === "affiliate" && (
            <a
              href={ad.link_url}
              target={ad.link_target}
              rel="noopener noreferrer sponsored"
              onClick={() => handleClick(ad)}
              className="block"
            >
              {ad.image_url ? (
                <img
                  src={ad.image_url}
                  alt={ad.alt_text || ad.name}
                  className="rounded-lg border border-border shadow-sm hover:shadow-md transition-shadow w-full object-contain"
                  style={{ maxHeight: ad.max_height || undefined }}
                />
              ) : (
                <div className="p-3 rounded-lg border border-border bg-card hover:bg-accent transition-colors text-sm">
                  <p className="font-medium">{ad.name}</p>
                </div>
              )}
            </a>
          )}

          {ad.ad_type === "html_embed" && ad.html_content && (
            <div
              className="rounded-lg border border-border overflow-hidden"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(ad.html_content, {
                ALLOWED_TAGS: ['a', 'b', 'i', 'em', 'strong', 'br', 'p', 'span', 'div', 'img'],
                ALLOWED_ATTR: ['href', 'target', 'class', 'style', 'src', 'alt', 'width', 'height', 'rel'],
                FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
                FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover']
              }) }}
            />
          )}

          {/* "Anzeige" label */}
          <p className="text-[9px] text-muted-foreground/50 text-center mt-1">Anzeige</p>
        </div>
      ))}
    </div>
  );
}
